import { toInputDate } from "@/utils/dateUtils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Download, Send, Edit, Save, X, Trash2, Upload } from "lucide-react";
import { FileUploadTrigger } from "@/components/native/FileUploadTrigger";
import React, { useState, useEffect } from "react";
import { ExtendedInvoice } from "@/types/payment";
import { useInvoice } from "@/hooks/useInvoice";
import { usePayment } from "@/hooks/usePayment";
import { toast } from "sonner";

import { useFile } from "@/hooks/useFile";
import { useMedia } from "@/hooks/useMedia";
import { formatMileage } from "@/utils/listingUtils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLanguage } from "@/context/LanguageContext";
import { ordersLabels, commonButtonLabels } from "@/i18n";

interface InvoicePopupProps {
  invoice: ExtendedInvoice | null;
  onClose: () => void;
  isAdmin?: boolean;
  onSendInvoice?: (id: string) => void;
}

const InvoicePopup = ({
  invoice,
  onClose,
  isAdmin = false,
  onSendInvoice,
}: InvoicePopupProps) => {
  const { language } = useLanguage();
  const o = ordersLabels(language);
  const p = o.invoicePopup as Record<string, string>;
  const btn = commonButtonLabels(language);
  const statusLabel = (status: string | undefined) => {
    switch (status) {
      case "paid":
        return o.invoice.paid;
      case "pending":
        return o.invoice.pending;
      case "overdue":
        return o.invoice.overdue;
      case "unpaid":
        return o.invoice.unpaid;
      case "cancelled":
        return o.invoice.cancelled;
      case "failed":
        return o.invoice.failed;
      default:
        return status || "";
    }
  };
  const {
    updateInvoiceAsync,
    deleteInvoiceAsync,
    isUpdatingInvoice,
    isDeletingInvoice,
  } = useInvoice();
  const { downloadFile } = useFile();

  const { getDownloadUrlAsync } = useMedia();

  const { updatePaymentAsync, isUpdatingPayment } = usePayment();

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    invoice_number: "",
    amount: "",
    status: "unpaid" as
      | "paid"
      | "pending"
      | "unpaid"
      | "failed"
      | "overdue"
      | "cancelled",
    due_date: "",
  });
  const [newInvoiceFile, setNewInvoiceFile] = useState<{
    id: string;
    url: string;
    name: string;
  } | null>(null);
  const [originalData, setOriginalData] = useState(formData);

  useEffect(() => {
    if (invoice) {
      const data = {
        invoice_number: invoice.invoice?.invoice_number || "",
        amount: invoice.payment?.amount?.toString() || "",
        status: invoice.payment?.status || "unpaid",
        due_date: invoice.payment?.due_date
          ? toInputDate(new Date(invoice.payment.due_date))
          : "",
      };
      setFormData(data);
      setOriginalData(data);
    }
  }, [invoice]);

  if (!invoice) return null;

  const hasChanges = () => {
    return (
      formData.invoice_number !== originalData.invoice_number ||
      formData.amount !== originalData.amount ||
      formData.status !== originalData.status ||
      formData.due_date !== originalData.due_date ||
      newInvoiceFile !== null
    );
  };

  const handleSave = async () => {
    if (!invoice || !hasChanges()) return;

    try {
      // Update invoice fields (invoice_number and file)
      if (
        formData.invoice_number !== originalData.invoice_number ||
        newInvoiceFile
      ) {
        await updateInvoiceAsync({
          id: invoice.invoice.id,
          invoice_number: formData.invoice_number,
          invoice_file: newInvoiceFile || invoice.invoice.invoice_file,
        });
      }

      // Update payment fields (amount, status, due_date)
      const paymentChanged =
        formData.amount !== originalData.amount ||
        formData.status !== originalData.status ||
        formData.due_date !== originalData.due_date;

      if (paymentChanged && invoice.payment) {
        await updatePaymentAsync({
          id: invoice.payment.id,
          listingId: invoice.payment.listingId,
          type: invoice.payment.type,
          status: formData.status as
            | "paid"
            | "pending"
            | "unpaid"
            | "failed"
            | "overdue"
            | "cancelled",
          amount: parseFloat(formData.amount) || 0,
          due_date: formData.due_date || null,
          paid_date: invoice.payment.paid_date,
          comment: invoice.payment.comment,
          payout_bankDetails: invoice.payment.payout_bankDetails,
          created_at: invoice.payment.created_at,
        });
      }

      setOriginalData(formData);
      setNewInvoiceFile(null);
      setIsEditing(false);
    } catch (error) {}
  };

  const handleCancel = () => {
    setFormData(originalData);
    setNewInvoiceFile(null);
    setIsEditing(false);
  };

  const handleDeleteInvoice = async () => {
    if (!invoice || !window.confirm(p.confirmDelete)) return;

    try {
      await deleteInvoiceAsync(invoice.invoice.id);
      onClose();
    } catch (error) {}
  };

  return (
    <Dialog open={!!invoice} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>{p.title}</DialogTitle>
            {isAdmin && (
              <div className="flex gap-2">
                {!isEditing ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                    className="gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    {btn.edit}
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancel}
                      className="gap-2"
                    >
                      <X className="h-4 w-4" />
                      {btn.cancel}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleDeleteInvoice}
                      disabled={isDeletingInvoice}
                      className="gap-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      {isDeletingInvoice ? btn.deleting : btn.delete}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </DialogHeader>
        <ScrollArea className="flex-1 max-h-[60vh]">
          <div className="space-y-6 p-1">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-primary/60">
                  {p.invoiceNumber}
                </Label>
                {isEditing ? (
                  <Input
                    value={formData.invoice_number}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        invoice_number: e.target.value,
                      }))
                    }
                    className="mt-1"
                  />
                ) : (
                  <p className="mt-1 text-sm">
                    {invoice.invoice?.invoice_number || p.nA}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-sm font-medium text-primary/60">
                  {p.amount}
                </Label>
                {isEditing ? (
                  <Input
                    type="number"
                    value={formData.amount}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        amount: e.target.value,
                      }))
                    }
                    className="mt-1"
                    placeholder={p.amountPlaceholder}
                  />
                ) : (
                  <p className="mt-1 text-sm font-medium text-green-600">
                    {invoice.payment?.amount?.toLocaleString()} kr
                  </p>
                )}
              </div>
              <div>
                <Label className="text-sm font-medium text-primary/60">
                  {p.status}
                </Label>
                {isEditing ? (
                  <Select
                    value={formData.status}
                    onValueChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        status: value as
                          | "paid"
                          | "pending"
                          | "unpaid"
                          | "failed"
                          | "overdue"
                          | "cancelled",
                      }))
                    }
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unpaid">{o.invoice.unpaid}</SelectItem>
                      <SelectItem value="paid">{o.invoice.paid}</SelectItem>
                      <SelectItem value="pending">
                        {o.invoice.pending}
                      </SelectItem>
                      <SelectItem value="overdue">
                        {o.invoice.overdue}
                      </SelectItem>
                      <SelectItem value="failed">{o.invoice.failed}</SelectItem>
                      <SelectItem value="cancelled">
                        {o.invoice.cancelled}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="mt-1 text-sm">
                    {statusLabel(invoice.payment?.status)}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-sm font-medium text-primary/60">
                  {p.createdDate}
                </Label>
                <p className="mt-1 text-sm">
                  {invoice.payment?.created_at
                    ? new Date(invoice.payment.created_at)
                        .toISOString()
                        .split("T")[0]
                    : ""}
                </p>
              </div>
              <div>
                <Label className="text-sm font-medium text-primary/60">
                  {p.dueDate}
                </Label>
                {isEditing ? (
                  <Input
                    type="date"
                    value={
                      formData.due_date
                        ? new Date(formData.due_date)
                            .toISOString()
                            .split("T")[0]
                        : ""
                    }
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        due_date: new Date(e.target.value)
                          .toISOString()
                          .split("T")[0],
                      }));
                    }}
                    className="mt-1"
                  />
                ) : (
                  <p className="mt-1 text-sm">
                    {invoice.payment?.due_date
                      ? new Date(invoice.payment.due_date)
                          .toISOString()
                          .split("T")[0]
                      : ""}
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="border rounded p-3 bg-muted">
                <div className="font-semibold mb-1">{p.customerInfo}</div>
                {invoice.customer ? (
                  <>
                    <div>
                      {p.name}: {invoice.customer?.firstName}{" "}
                      {invoice.customer?.lastName}
                    </div>
                    <div>
                      {p.email}: {invoice.customer?.email}
                    </div>
                    <div>
                      {p.userType}: {invoice.customer?.role}
                    </div>
                  </>
                ) : (
                  <div className="text-muted-foreground text-sm">
                    {p.noCustomerInfo}
                  </div>
                )}
              </div>
              <div className="border rounded p-3 bg-muted">
                <div className="font-semibold mb-1">{p.vehicle}</div>
                {invoice.listing ? (
                  <>
                    <div>
                      {p.regNumber}: {invoice.listing.regNumber}
                    </div>
                    <div>
                      {p.make}: {invoice.listing.make}
                    </div>
                    <div>
                      {p.model}: {invoice.listing.model}
                    </div>
                    <div>
                      {p.year}: {invoice.listing.year}
                    </div>
                    <div>
                      {p.color}: {invoice.listing.color}
                    </div>
                    <div>
                      {p.mileage}: {formatMileage(invoice.listing.mileage)}{" "}
                      {p.miles}
                    </div>
                  </>
                ) : (
                  <div className="text-muted-foreground text-sm">
                    {p.noVehicleInfo}
                  </div>
                )}
              </div>
            </div>
            <div className="border-t pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-primary/60">
                  {p.documents}
                </h3>
                {isEditing && (
                  <FileUploadTrigger
                    accept="application/pdf,image/*"
                    onFilesSelected={(files) => {
                      const file = files[0];
                      if (!file) return;
                      const mockFileData = {
                        id: `file_${Date.now()}`,
                        url: URL.createObjectURL(file),
                        name: file.name,
                      };
                      setNewInvoiceFile(mockFileData);
                    }}
                  >
                    {(open) => (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={open}
                        className="gap-2"
                      >
                        <Upload className="h-4 w-4" />
                        {p.uploadNewFile}
                      </Button>
                    )}
                  </FileUploadTrigger>
                )}
              </div>
              <div className="space-y-2">
                {newInvoiceFile && (
                  <div className="flex items-center justify-between p-2 rounded-lg border bg-blue-50">
                    <span className="text-sm text-blue-700">
                      {p.newFileSelected}: {newInvoiceFile.name}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setNewInvoiceFile(null)}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                {invoice.invoice?.invoice_file && !newInvoiceFile && (
                  <div className="flex items-center justify-between p-2 rounded-lg border">
                    <span className="text-sm">
                      {p.invoiceFile} ({invoice.invoice?.invoice_file.name})
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={async () => {
                          if (invoice.invoice?.invoice_file) {
                            try {
                              // If mediaId exists, fetch presigned URL on-demand via hook
                              let downloadUrl =
                                invoice.invoice.invoice_file.url;
                              if (
                                invoice.invoice.invoice_file.mediaId &&
                                !invoice.invoice.invoice_file.url
                              ) {
                                downloadUrl = await getDownloadUrlAsync({
                                  mediaId: invoice.invoice.invoice_file.mediaId,
                                  expiresIn: 3600,
                                });
                              }
                              const filename = await downloadFile(
                                downloadUrl,
                                invoice.invoice.invoice_file.name,
                              );
                              toast.success(p.downloadStarted, {
                                description: p.downloadDescription.replace(
                                  "{filename}",
                                  filename,
                                ),
                              });
                            } catch (error) {
                              const errorMessage =
                                error instanceof Error
                                  ? error.message
                                  : p.unknownError;
                              toast.error(p.downloadError, { description: `${invoice.invoice.invoice_file.name}\n${p.errorLabel}: ${errorMessage}` });
                            }
                          }
                        }}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      {isEditing && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={handleDeleteInvoice}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </ScrollArea>
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
          {isAdmin && (
            <>
              {!isEditing ? (
                <Button
                  variant="secondary"
                  className="gap-2"
                  onClick={() => {
                    if (onSendInvoice && invoice) {
                      onSendInvoice(invoice.invoice?.id);
                    }
                  }}
                >
                  <Send className="h-4 w-4" /> {p.sendInvoice}
                </Button>
              ) : (
                <Button
                  variant="default"
                  className="gap-2"
                  onClick={handleSave}
                  disabled={
                    !hasChanges() || isUpdatingInvoice || isUpdatingPayment
                  }
                >
                  <Save className="h-4 w-4" />
                  {isUpdatingInvoice || isUpdatingPayment
                    ? p.saving
                    : p.saveChanges}
                </Button>
              )}
            </>
          )}
          <Button variant="outline" onClick={onClose}>
            {p.close}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InvoicePopup;
